import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { TripStatus } from '@raahi/shared';
import { RealtimeEvents } from '@raahi/shared';
import { Shell } from '../layout/Shell.js';
import { MapView } from '../components/MapView.js';
import { BottomSheet } from '../components/BottomSheet.js';
import { StatusPill } from '../components/StatusPill.js';
import { DriverCard } from '../components/DriverCard.js';
import { FareBreakdownView } from '../components/FareBreakdown.js';
import { Pulse } from '../components/Pulse.js';
import { useActiveTrip } from '../state/activeTrip.js';
import { useSocket } from '../hooks/useSocket.js';
import { useToast } from '../components/Toast.js';
import { cancelTrip, getTrip, rateTrip, type TripDto } from '../api/trips.js';

const TERMINAL: TripStatus[] = ['completed', 'cancelled'];

function StageCopy({ status }: { status: TripStatus }) {
  const copy: Record<TripStatus, string> = {
    requested: 'Looking for a driver…',
    searching: 'Looking for a driver…',
    assigned: 'A driver is on the way.',
    arriving: 'Your driver is heading to you.',
    arrived: 'Your driver has arrived.',
    in_progress: 'Enjoy the ride.',
    completed: 'Trip completed.',
    cancelled: 'Trip cancelled.',
  };
  return <p className="text-sm text-muted">{copy[status]}</p>;
}

export function ActiveTripPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tripId = useActiveTrip((s) => s.tripId);
  const clearTrip = useActiveTrip((s) => s.setTripId);
  const socket = useSocket();
  const toast = useToast();
  const lastStatusRef = useRef<TripStatus | null>(null);
  const [liveDriver, setLiveDriver] = useState<{ lat: number; lng: number } | null>(null);
  const [rating, setRating] = useState<number>(5);
  const [ratingSent, setRatingSent] = useState(false);

  useEffect(() => {
    if (!tripId) {
      navigate('/', { replace: true });
    }
  }, [tripId, navigate]);

  const trip = useQuery({
    queryKey: ['trip', tripId],
    enabled: Boolean(tripId),
    queryFn: () => getTrip(tripId as string),
    refetchInterval: (q) => {
      const status = (q.state.data as TripDto | undefined)?.status as TripStatus | undefined;
      if (!status || TERMINAL.includes(status)) return false;
      return 5000;
    },
  });

  useEffect(() => {
    if (!socket || !tripId) return;
    socket.emit('trip:join', { tripId });
    const onUpdate = () => queryClient.invalidateQueries({ queryKey: ['trip', tripId] });
    const onLocation = (payload: { position: { lat: number; lng: number } }) => {
      setLiveDriver(payload.position);
    };
    socket.on(RealtimeEvents.TripUpdate, onUpdate);
    socket.on(RealtimeEvents.TripFare, onUpdate);
    socket.on(RealtimeEvents.DriverLocation, onLocation);
    return () => {
      socket.emit('trip:leave', { tripId });
      socket.off(RealtimeEvents.TripUpdate, onUpdate);
      socket.off(RealtimeEvents.TripFare, onUpdate);
      socket.off(RealtimeEvents.DriverLocation, onLocation);
    };
  }, [socket, tripId, queryClient]);

  const cancel = useMutation({
    mutationFn: () => cancelTrip(tripId as string, 'Rider cancelled'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trip', tripId] }),
  });

  const rate = useMutation({
    mutationFn: () => rateTrip(tripId as string, rating),
    onSuccess: () => setRatingSent(true),
  });

  const status = (trip.data?.status ?? 'requested') as TripStatus;
  const isTerminal = TERMINAL.includes(status);

  useEffect(() => {
    if (lastStatusRef.current === status) return;
    const messages: Partial<Record<TripStatus, { msg: string; tone: 'info' | 'success' | 'warn' | 'error' }>> = {
      assigned: { msg: 'Driver assigned. They are on the way.', tone: 'info' },
      arrived: { msg: 'Your driver has arrived.', tone: 'success' },
      in_progress: { msg: 'Trip started. Have a safe ride.', tone: 'info' },
      completed: { msg: 'Trip completed. Please rate your driver.', tone: 'success' },
      cancelled: { msg: 'This trip was cancelled.', tone: 'warn' },
    };
    const m = messages[status];
    if (m && lastStatusRef.current !== null) toast.show(m.msg, m.tone);
    lastStatusRef.current = status;
  }, [status, toast]);

  const centre = useMemo(() => {
    if (liveDriver) return liveDriver;
    if (trip.data) return trip.data.pickup;
    return { lat: 24.86, lng: 67.01 };
  }, [liveDriver, trip.data]);

  return (
    <Shell fullBleed hideTabBar>
      <div className="absolute inset-0">
        <MapView
          centre={centre}
          pickup={trip.data?.pickup}
          drop={trip.data?.drop}
          liveDriver={liveDriver}
          follow={liveDriver ? 'driver' : 'route'}
        />
      </div>

      <header className="pointer-events-none absolute inset-x-0 top-0 z-[1000] flex justify-center px-4 pt-[max(env(safe-area-inset-top),12px)]">
        <div className="pointer-events-auto flex w-full max-w-mobile items-center justify-between rounded-2xl bg-white/95 px-4 py-2 shadow-card backdrop-blur">
          <div className="flex items-center gap-2">
            {!isTerminal ? <Pulse /> : null}
            <StatusPill status={status} />
          </div>
          {isTerminal ? (
            <button
              type="button"
              className="text-sm font-semibold text-ink"
              onClick={() => {
                clearTrip(null);
                navigate('/', { replace: true });
              }}
            >
              Done
            </button>
          ) : null}
        </div>
      </header>

      <BottomSheet>
        {trip.isLoading ? (
          <p className="text-sm text-muted">Loading your trip…</p>
        ) : trip.data ? (
          <div className="space-y-3">
            <StageCopy status={status} />

            {trip.data.driver ? <DriverCard driver={trip.data.driver} /> : null}

            {status === 'completed' ? (
              <>
                <FareBreakdownView fare={trip.data.fare} paymentMethod={trip.data.paymentMethod} />
                {ratingSent ? (
                  <p className="text-sm font-medium text-ink">Thanks for the feedback.</p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Rate your driver</p>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setRating(n)}
                          aria-label={`${n} star${n === 1 ? '' : 's'}`}
                          className={
                            'h-10 w-10 rounded-full text-base font-semibold ' +
                            (rating >= n ? 'bg-ink text-white' : 'bg-line text-ink')
                          }
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn-primary w-full"
                      disabled={rate.isPending}
                      onClick={() => rate.mutate()}
                    >
                      {rate.isPending ? 'Sending…' : 'Submit rating'}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="num space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted">Pickup</span>
                  <span className="font-medium">{trip.data.pickup.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Drop-off</span>
                  <span className="font-medium">{trip.data.drop.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Fare</span>
                  <span className="font-semibold">Rs {trip.data.fare.total.toLocaleString('en-PK')}</span>
                </div>
              </div>
            )}

            {!isTerminal && status !== 'in_progress' ? (
              <button
                type="button"
                className="btn-ghost w-full text-red-600"
                onClick={() => cancel.mutate()}
                disabled={cancel.isPending}
              >
                {cancel.isPending ? 'Cancelling…' : 'Cancel ride'}
              </button>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted">Trip not found.</p>
        )}
      </BottomSheet>
    </Shell>
  );
}
